import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { useEffect, useState, useMemo } from "react";
import EditIcon from '@mui/icons-material/Edit';
import { Plus, Trash2 } from "lucide-react";
import { Checkbox, Tooltip } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { notFoundUrl } from "@/routes/urls";
import { isValidGUID } from "@/utils/validation";
import { LiquidityScenario, LiquidityScenarioTransaction, TransactionOrigin, TransactionStatus, TransactionTypeCodes } from "@/types/liquidity-api";
import dayjs from "dayjs";
import { formatNumber } from "@/utils/format";
import { EditTransactionDialog } from "./EditTransactionDialog";
import { useUpdateLiquidityScenarioTransactions } from "@/hooks/use-liquidity-scenario";
import Card from "@/components/ui/Card";
interface LiquidityTransactionsTableProps {
	selectedLiquidityScenario: LiquidityScenario;
}

const LiquidityTransactionsTable = ({ selectedLiquidityScenario }: LiquidityTransactionsTableProps) => {
	const navigate = useNavigate();
	const { clientId, connectionId } = useParams<{ clientId: string; connectionId: string }>();

	useEffect(() => {
		if (!clientId || !connectionId || !isValidGUID(clientId) || !isValidGUID(connectionId)) {
			navigate(notFoundUrl);
		}
	}, [navigate, clientId, connectionId]);

	const [editingTransaction, setEditingTransaction] = useState<LiquidityScenarioTransaction | null>(null);
	const isEditTransactionDialogOpen = !!editingTransaction;

	const [createNewTransactionDialogOpen, setCreateNewTransactionDialogOpen] = useState(false);

	// Add sort state
	type SortField = 'otherPart' | 'transactionType' | 'transactionDate' | 'amount' | 'origin' | 'transactionTypeCode' | 'invoiceNumber';
	type SortDirection = 'asc' | 'desc';
	const [sortConfig, setSortConfig] = useState<{ field: SortField; direction: SortDirection }>({
		field: 'transactionDate',
		direction: 'asc'
	});

	// Hooks
	const { mutate: updateLiquidityScenarioTransactions } = useUpdateLiquidityScenarioTransactions(clientId, connectionId);

	const updateTransaction = (transaction: LiquidityScenarioTransaction) => {
		// This transactions exists in the selectedLiquidityScenario, so we need to update it.
		// Remove the transaction from the selectedLiquidityScenario.transactions array and add the updated transaction.
		const updatedTransactions = selectedLiquidityScenario.transactions.filter(t => t.key !== transaction.key);
		updatedTransactions.push(transaction);
		updateLiquidityScenarioTransactions({
			liquidityScenarioId: selectedLiquidityScenario.id,
			transactions: updatedTransactions
		});
	}

	const saveTransaction = (transaction: LiquidityScenarioTransaction) => {
		updateLiquidityScenarioTransactions({
			liquidityScenarioId: selectedLiquidityScenario.id,
			transactions: [...selectedLiquidityScenario.transactions, transaction]
		});
	}

	const deleteTransaction = (transaction: LiquidityScenarioTransaction) => {
		const updatedTransactions = selectedLiquidityScenario.transactions.filter(t => t.key !== transaction.key);
		updateLiquidityScenarioTransactions({
			liquidityScenarioId: selectedLiquidityScenario.id,
			transactions: updatedTransactions
		});
	}

	const toggleTransactionStatus = (transaction: LiquidityScenarioTransaction) => {
		const currentStatus = transaction.status;
		const newStatus = currentStatus === TransactionStatus.Pending ? TransactionStatus.Discarded : TransactionStatus.Pending;
		const updatedTransactions = selectedLiquidityScenario.transactions.map(t => t.key === transaction.key ? { ...t, status: newStatus } : t);
		updateLiquidityScenarioTransactions({
			liquidityScenarioId: selectedLiquidityScenario.id,
			transactions: updatedTransactions
		});
	}

	const compareAmounts = (a: LiquidityScenarioTransaction, b: LiquidityScenarioTransaction, direction: number) => {
		const amountA = a.modifiedAmount ?? a.amount ?? 0;
		const amountB = b.modifiedAmount ?? b.amount ?? 0;
		return direction * (amountA - amountB);
	};

	const sortedTransactions = useMemo(() => {
		return [...selectedLiquidityScenario.transactions].sort((a, b) => {
			const direction = sortConfig.direction === 'asc' ? 1 : -1;
			const maybeInvoiceNumberA = a.invoiceNumber ? a.invoiceNumber : a.externalInvoiceNumber ? a.externalInvoiceNumber : undefined;
			const maybeInvoiceNumberB = b.invoiceNumber ? b.invoiceNumber : b.externalInvoiceNumber ? b.externalInvoiceNumber : undefined;
			const transactionDateA = a.modifiedTransactionDate || a.transactionDate || '';
			const transactionDateB = b.modifiedTransactionDate || b.transactionDate || '';
			switch (sortConfig.field) {
				case 'otherPart':
					return direction * (a.otherPart?.localeCompare(b.otherPart || '') || 0);
				case 'transactionType':
					return direction * (a.transactionType - b.transactionType);
				case 'transactionDate':
					return direction * (dayjs(transactionDateA).unix() - dayjs(transactionDateB).unix());
				case 'amount':
					return compareAmounts(a, b, direction);
				case 'origin':
					return direction * (a.origin - b.origin);
				case 'transactionTypeCode':
					return direction * (a.transactionTypeCode?.localeCompare(b.transactionTypeCode || '') || 0);
				case 'invoiceNumber':
					return direction * (maybeInvoiceNumberA?.localeCompare(maybeInvoiceNumberB || '') || 0);
				default:
					return 0;
			}
		});
	}, [selectedLiquidityScenario.transactions, sortConfig.field, sortConfig.direction]);

	const handleSort = (field: SortField) => {
		setSortConfig(current => ({
			field,
			direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
		}));
	};

	return (
		<Card className="border border-palette-dark-gray rounded-lg" hoverable={false}>
			<div>
				<div className="p-6">
					<div className="flex mb-6 items-center justify-between">
						<h2 className="text-2xl">Periodens transaktioner</h2>
						<Button variant="outlined" color='secondary' startIcon={<Plus />}
							onClick={() => setCreateNewTransactionDialogOpen(true)}>
							LÄGG TILL
						</Button>
					</div>

					<table className="w-full">
						<thead>
							<tr className="border-b">
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('invoiceNumber')}
								>
									Fakturanummer {sortConfig.field === 'invoiceNumber' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('otherPart')}
								>
									Transaktion {sortConfig.field === 'otherPart' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('transactionType')}
								>
									Typ {sortConfig.field === 'transactionType' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('transactionTypeCode')}
								>
									Kod {sortConfig.field === 'transactionTypeCode' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('transactionDate')}
								>
									Datum {sortConfig.field === 'transactionDate' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('amount')}
								>
									Summa {sortConfig.field === 'amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th
									className="p-4 text-left cursor-pointer hover:bg-gray-50"
									onClick={() => handleSort('origin')}
								>
									Källa {sortConfig.field === 'origin' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
								</th>
								<th className="p-4">Actions</th>
							</tr>
						</thead>

						<tbody>
							{sortedTransactions.map((transaction) => {
								const isEdited = transaction.modifiedAmount !== null || transaction.modifiedTransactionDate !== null;
								const isManualTransaction = transaction.origin === TransactionOrigin.Manual;
								const isChecked = transaction.status === TransactionStatus.Pending;
								const codeLabel = Object.values(TransactionTypeCodes).find(code => code.value === transaction.transactionTypeCode)?.label || ''
								const maybeInvoiceNumber = transaction.invoiceNumber ? transaction.invoiceNumber : transaction.externalInvoiceNumber ? transaction.externalInvoiceNumber : undefined;
								const invoiceNumber = maybeInvoiceNumber && maybeInvoiceNumber !== 'n/a' ? maybeInvoiceNumber : '';
								return (
									<tr key={transaction.key} className="border-b hover:bg-gray-50">
										<td className="p-4 text-center">{invoiceNumber}</td>
										<td className="p-4 flex items-center gap-4">
											{transaction.otherPart}
											{isEdited ? (
												<span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Edited</span>
											) : null}
										</td>
										<td className="p-4">
											{transaction.transactionType === 1 ? 'Inbetalning' : 'Utbetalning'}
										</td>
										<td className="p-4">
											{codeLabel}
										</td>
										<td className="p-4">
											{transaction.modifiedTransactionDate ? dayjs(transaction.modifiedTransactionDate).format('YYYY-MM-DD') : dayjs(transaction.transactionDate).format('YYYY-MM-DD')}
										</td>
										<td className="p-4">{transaction.modifiedAmount ? formatNumber(transaction.modifiedAmount) : formatNumber(transaction.amount)} kr</td>
										<td className="p-4">
											{isManualTransaction ? 'Manuell' : 'Fortnox'}
										</td>
										<td className="p-4 flex items-center gap-0 space-between justify-center">
											<Tooltip title="Visa eller dölj transaktion">
												<Checkbox
													color="primary"
													checked={isChecked}
													onChange={(e) => {
														toggleTransactionStatus(transaction);
													}}
												/>
											</Tooltip>

											<Tooltip title="Redigera transaktion">
												<IconButton
													size="small"
													color="primary"
													onClick={() => setEditingTransaction(transaction)}
												>
													<EditIcon />
												</IconButton>
											</Tooltip>

											<Tooltip title={isManualTransaction ? "Radera transaktion" : "Endast manuella transaktioner kan raderas"}>
												<span>
													<IconButton
														size="small"
														color="warning"
														disabled={!isManualTransaction}
														onClick={() => {
															deleteTransaction(transaction);
														}}
													>
														<Trash2 />
													</IconButton>
												</span>
											</Tooltip>

										</td>
									</tr>
								)
							})}
						</tbody>
					</table>

				</div>
			</div>

			{
				isEditTransactionDialogOpen && <EditTransactionDialog
					isOpen={!!editingTransaction}
					closeFunction={() => setEditingTransaction(null)}
					transaction={editingTransaction}
					isCreateNewTransaction={false}
					saveTransactionFunction={updateTransaction}
				/>
			}

			{
				createNewTransactionDialogOpen && <EditTransactionDialog
					isOpen={createNewTransactionDialogOpen}
					closeFunction={() => setCreateNewTransactionDialogOpen(false)}
					isCreateNewTransaction={true}
					saveTransactionFunction={saveTransaction}
				/>
			}
		</Card>

	);
};

export default LiquidityTransactionsTable;
